import { useState, useEffect, useMemo } from "react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Receipt, Loader2, Clock, CheckCircle2, AlertCircle,
  FileText, DollarSign, Calendar, ChevronRight, Download,
  Shield, User, CreditCard, Plus, ExternalLink, CheckSquare, Square, XCircle
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import axios from "axios";
import { toast } from "sonner";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";

const API = process.env.REACT_APP_BACKEND_URL;

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Card Element Options
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#1e293b',
      '::placeholder': { color: '#94a3b8' },
    },
    invalid: { color: '#ef4444' },
  },
};

// Payment Form Component — supports single invoice or batch (invoices array)
const PaymentForm = ({ invoice, invoices: batchInvoices, paymentMethods, token, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [useNewCard, setUseNewCard] = useState(paymentMethods.length === 0);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(
    paymentMethods.length > 0 ? paymentMethods[0].id : null
  );

  const isBatch = batchInvoices && batchInvoices.length > 1;
  const totalAmount = isBatch
    ? batchInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0)
    : invoice?.total || 0;
  const invoiceIds = isBatch
    ? batchInvoices.map((inv) => inv.invoice_id)
    : [invoice?.invoice_id];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe) {
      toast.error("Stripe not initialized");
      return;
    }

    setProcessing(true);
    const headers = { Authorization: `Bearer ${token}` };

    // Choose endpoint based on batch vs single
    const payUrl = isBatch
      ? `${API}/invoices/batch/pay-with-stripe`
      : `${API}/invoices/${invoice.invoice_id}/pay-with-stripe`;
    const confirmUrl = isBatch
      ? `${API}/invoices/batch/confirm-payment`
      : `${API}/invoices/${invoice.invoice_id}/confirm-payment`;

    try {
      if (useNewCard) {
        const body = isBatch ? { invoice_ids: invoiceIds } : {};
        const response = await axios.post(payUrl, body, { headers });

        if (response.data.client_secret) {
          const cardElement = elements.getElement(CardElement);
          const { error, paymentIntent } = await stripe.confirmCardPayment(
            response.data.client_secret,
            { payment_method: { card: cardElement } }
          );

          if (error) {
            toast.error(error.message);
            setProcessing(false);
            return;
          }

          if (paymentIntent.status === 'succeeded') {
            const confirmBody = isBatch
              ? { payment_intent_id: paymentIntent.id, invoice_ids: invoiceIds }
              : { payment_intent_id: paymentIntent.id };
            await axios.post(confirmUrl, confirmBody, { headers });
            toast.success(isBatch ? `${invoiceIds.length} invoices paid!` : "Payment successful!");
            onSuccess();
          }
        }
      } else {
        const body = isBatch
          ? { invoice_ids: invoiceIds, payment_method_id: selectedPaymentMethod }
          : { payment_method_id: selectedPaymentMethod };
        const response = await axios.post(payUrl, body, { headers });

        if (response.data.status === 'paid') {
          toast.success(isBatch ? `${invoiceIds.length} invoices paid!` : "Payment successful!");
          onSuccess();
        } else {
          toast.error("Payment failed. Please try again.");
        }
      }
    } catch (error) {
      console.error("Payment error:", error);
      toast.error(error.response?.data?.detail || "Payment failed");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {paymentMethods.length > 0 && (
        <div className="space-y-3">
          <label className="text-sm font-medium text-slate-700">Payment Method</label>
          {paymentMethods.map((pm) => (
            <div
              key={pm.id}
              onClick={() => {
                setSelectedPaymentMethod(pm.id);
                setUseNewCard(false);
              }}
              className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition-all ${
                !useNewCard && selectedPaymentMethod === pm.id
                  ? 'border-slate-900 bg-slate-50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <CreditCard className="w-5 h-5 text-slate-600" />
              <div className="flex-1">
                <p className="font-medium text-slate-900 capitalize">{pm.card.brand} •••• {pm.card.last4}</p>
                <p className="text-xs text-slate-500">Expires {pm.card.exp_month}/{pm.card.exp_year}</p>
              </div>
              {!useNewCard && selectedPaymentMethod === pm.id && (
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              )}
            </div>
          ))}
          <div
            onClick={() => setUseNewCard(true)}
            className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition-all ${
              useNewCard ? 'border-slate-900 bg-slate-50' : 'border-slate-200 hover:border-slate-300'
            }`}
          >
            <Plus className="w-5 h-5 text-slate-600" />
            <span className="font-medium text-slate-900">Use a new card</span>
            {useNewCard && <CheckCircle2 className="w-5 h-5 text-emerald-600 ml-auto" />}
          </div>
        </div>
      )}

      {(useNewCard || paymentMethods.length === 0) && (
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-700">Card Details</label>
          <div className="border border-slate-200 rounded-xl p-4">
            <CardElement options={cardElementOptions} />
          </div>
        </div>
      )}

      {/* Batch summary */}
      {isBatch && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 space-y-1">
          <p className="text-sm font-medium text-blue-900">Paying {batchInvoices.length} invoices</p>
          {batchInvoices.map((inv) => (
            <div key={inv.invoice_id} className="flex justify-between text-xs text-blue-700">
              <span>#{inv.invoice_number}</span>
              <span>${inv.total?.toFixed(2)}</span>
            </div>
          ))}
        </div>
      )}

      <div className="bg-slate-50 rounded-xl p-4">
        <div className="flex justify-between text-sm">
          <span className="text-slate-600">{isBatch ? 'Total Amount' : 'Amount Due'}</span>
          <span className="font-bold text-slate-900 text-lg">${totalAmount.toFixed(2)}</span>
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1 rounded-xl" disabled={processing}>
          Cancel
        </Button>
        <Button type="submit" className="flex-1 bg-slate-900 text-white hover:bg-slate-800 rounded-xl" disabled={processing || !stripe}>
          {processing ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</>
          ) : (
            <><DollarSign className="w-4 h-4 mr-2" />Pay ${totalAmount.toFixed(2)}</>
          )}
        </Button>
      </div>
    </form>
  );
};

export const InvoicesTab = ({ token }) => {
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [filter, setFilter] = useState("all");
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [payingInvoice, setPayingInvoice] = useState(null);
  const [payingBatch, setPayingBatch] = useState(null); // array of invoices for batch pay
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set()); // multi-select for batch pay

  useEffect(() => {
    fetchInvoices();
  }, [token]);

  const fetchInvoices = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      // Fetch both platform invoices and org-created invoices for parents
      const [platformRes, orgRes] = await Promise.all([
        axios.get(`${API}/invoices`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/org-invoices/my`, { headers }).catch(() => ({ data: [] })),
      ]);
      const platform = platformRes.data || [];
      const org = orgRes.data || [];
      // Merge and deduplicate by invoice_id
      const seen = new Set();
      const merged = [...org, ...platform].filter((inv) => {
        if (seen.has(inv.invoice_id)) return false;
        seen.add(inv.invoice_id);
        return true;
      });
      // Sort newest first
      merged.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setInvoices(merged);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'pending': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'overdue': return 'bg-red-50 text-red-700 border-red-200';
      default: return 'bg-slate-50 text-slate-700 border-slate-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid': return <CheckCircle2 className="w-4 h-4" />;
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'overdue': return <AlertCircle className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const filteredInvoices = invoices.filter(inv => filter === 'all' || inv.status === filter);
  const totalPending = invoices.filter(inv => inv.status === 'pending').reduce((sum, inv) => sum + (inv.total || 0), 0);
  const totalPaid = invoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + (inv.total || 0), 0);

  const pendingInvoices = useMemo(() => invoices.filter(inv => inv.status === 'pending'), [invoices]);
  const pendingFilteredIds = useMemo(
    () => filteredInvoices.filter(inv => inv.status === 'pending').map(inv => inv.invoice_id),
    [filteredInvoices]
  );
  const selectedCount = selectedIds.size;
  const selectedTotal = useMemo(
    () => invoices.filter(inv => selectedIds.has(inv.invoice_id)).reduce((sum, inv) => sum + (inv.total || 0), 0),
    [invoices, selectedIds]
  );

  const toggleSelect = (invoiceId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(invoiceId)) next.delete(invoiceId);
      else next.add(invoiceId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (pendingFilteredIds.every((id) => selectedIds.has(id))) {
      // Deselect all visible pending
      setSelectedIds((prev) => {
        const next = new Set(prev);
        pendingFilteredIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        pendingFilteredIds.forEach((id) => next.add(id));
        return next;
      });
    }
  };

  const openBatchPay = async () => {
    const selected = invoices.filter((inv) => selectedIds.has(inv.invoice_id) && inv.status === 'pending');
    if (selected.length === 0) return;

    setPayingBatch(selected);
    setPayingInvoice(null);
    setLoadingPaymentMethods(true);

    try {
      const headers = { Authorization: `Bearer ${token}` };
      // Use first invoice to fetch payment methods
      const res = await axios.get(`${API}/invoices/${selected[0].invoice_id}/payment-methods`, { headers });
      setPaymentMethods(res.data.payment_methods || []);
    } catch (error) {
      console.error("Error fetching payment methods:", error);
      setPaymentMethods([]);
    } finally {
      setLoadingPaymentMethods(false);
      setPaymentDialog(true);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="invoices-tab">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">${totalPending.toFixed(2)}</p>
              <p className="text-sm text-slate-500">Outstanding</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">${totalPaid.toFixed(2)}</p>
              <p className="text-sm text-slate-500">Paid</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
              <Receipt className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{invoices.length}</p>
              <p className="text-sm text-slate-500">Total Invoices</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 bg-slate-100 p-1 rounded-xl w-fit">
        {[
          { value: 'all', label: 'All' },
          { value: 'pending', label: 'Pending' },
          { value: 'paid', label: 'Paid' },
          { value: 'overdue', label: 'Overdue' }
        ].map((tab) => (
          <button
            key={tab.value}
            onClick={() => setFilter(tab.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === tab.value
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            {tab.label}
            {tab.value === 'pending' && invoices.filter(i => i.status === 'pending').length > 0 && (
              <span className="ml-2 px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full">
                {invoices.filter(i => i.status === 'pending').length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Invoices List */}
      {filteredInvoices.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Receipt className="w-8 h-8 text-slate-400" />
          </div>
          <h3 className="font-semibold text-slate-900 mb-2">No Invoices</h3>
          <p className="text-slate-500">
            {filter === 'all' ? "You don't have any invoices yet" : `No ${filter} invoices found`}
          </p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          {/* Select All Header */}
          {pendingFilteredIds.length > 1 && (
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center gap-3">
              <button
                onClick={(e) => { e.stopPropagation(); toggleSelectAll(); }}
                className="text-slate-500 hover:text-slate-700 transition-colors"
              >
                {pendingFilteredIds.every((id) => selectedIds.has(id)) ? (
                  <CheckSquare className="w-5 h-5 text-slate-900" />
                ) : (
                  <Square className="w-5 h-5" />
                )}
              </button>
              <span className="text-sm text-slate-600">
                Select all pending ({pendingFilteredIds.length})
              </span>
            </div>
          )}
          <div className="divide-y divide-slate-100">
            {filteredInvoices.map((invoice) => (
              <div 
                key={invoice.invoice_id}
                className={`p-4 hover:bg-slate-50 cursor-pointer transition-colors ${
                  selectedIds.has(invoice.invoice_id) ? 'bg-slate-50 ring-1 ring-inset ring-slate-300' : ''
                }`}
                onClick={() => setSelectedInvoice(selectedInvoice?.invoice_id === invoice.invoice_id ? null : invoice)}
                data-testid={`invoice-${invoice.invoice_id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    {/* Checkbox for pending invoices */}
                    {invoice.status === 'pending' && (
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleSelect(invoice.invoice_id); }}
                        className="mt-3 text-slate-400 hover:text-slate-700 transition-colors flex-shrink-0"
                      >
                        {selectedIds.has(invoice.invoice_id) ? (
                          <CheckSquare className="w-5 h-5 text-slate-900" />
                        ) : (
                          <Square className="w-5 h-5" />
                        )}
                      </button>
                    )}
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      invoice.related_to?.type === 'background_check' ? 'bg-blue-50' : 'bg-slate-100'
                    }`}>
                      {invoice.related_to?.type === 'background_check' ? (
                        <Shield className="w-6 h-6 text-blue-600" />
                      ) : (
                        <Receipt className="w-6 h-6 text-slate-400" />
                      )}
                    </div>
                    
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-slate-900">{invoice.description}</p>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(invoice.status)}`}>
                          {getStatusIcon(invoice.status)}
                          {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500 mt-1">Invoice #{invoice.invoice_number}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {new Date(invoice.created_at).toLocaleDateString('en-US', {
                            month: 'short', day: 'numeric', year: 'numeric'
                          })}
                        </span>
                        {invoice.due_date && invoice.status === 'pending' && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            Due: {new Date(invoice.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <p className="text-xl font-bold text-slate-900">${invoice.total?.toFixed(2)}</p>
                    <ChevronRight className={`w-5 h-5 text-slate-400 mt-2 transition-transform ${
                      selectedInvoice?.invoice_id === invoice.invoice_id ? 'rotate-90' : ''
                    }`} />
                  </div>
                </div>
                
                {/* Expanded Details */}
                {selectedInvoice?.invoice_id === invoice.invoice_id && (
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <div className="bg-slate-50 rounded-xl p-4 mb-4">
                      <h4 className="text-sm font-medium text-slate-700 mb-3">Line Items</h4>
                      <div className="space-y-2">
                        {invoice.line_items?.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2">
                              <span className="text-slate-600">{item.description}</span>
                              {item.quantity > 1 && <span className="text-slate-400">x{item.quantity}</span>}
                            </div>
                            <span className="font-medium text-slate-900">${item.total?.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                      <div className="mt-3 pt-3 border-t border-slate-200 flex justify-between text-sm font-semibold">
                        <span className="text-slate-700">Total</span>
                        <span className="text-slate-900">${invoice.total?.toFixed(2)}</span>
                      </div>
                    </div>
                    
                    {invoice.related_to?.type === 'background_check' && (
                      <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
                        <User className="w-4 h-4" />
                        <span>Staff: {invoice.related_to.staff_name} ({invoice.related_to.staff_email})</span>
                      </div>
                    )}
                    
                    {invoice.notes && <p className="text-sm text-slate-500 mb-4">{invoice.notes}</p>}
                    
                    {invoice.status === 'pending' && (
                      <div className="flex gap-2">
                        <Button 
                          className="bg-slate-900 text-white hover:bg-slate-800 rounded-xl"
                          onClick={async (e) => {
                            e.stopPropagation();
                            setPayingInvoice(invoice);
                            setLoadingPaymentMethods(true);
                            
                            try {
                              const headers = { Authorization: `Bearer ${token}` };
                              const res = await axios.get(`${API}/invoices/${invoice.invoice_id}/payment-methods`, { headers });
                              setPaymentMethods(res.data.payment_methods || []);
                            } catch (error) {
                              console.error("Error fetching payment methods:", error);
                              setPaymentMethods([]);
                            } finally {
                              setLoadingPaymentMethods(false);
                              setPaymentDialog(true);
                            }
                          }}
                        >
                          <CreditCard className="w-4 h-4 mr-2" />
                          Pay Now
                        </Button>
                        <Button variant="outline" className="rounded-xl" onClick={(e) => { e.stopPropagation(); toast.info("Download feature coming soon"); }}>
                          <Download className="w-4 h-4 mr-2" />
                          Download PDF
                        </Button>
                      </div>
                    )}
                    
                    {invoice.status === 'paid' && invoice.paid_at && (
                      <div className="flex items-center gap-2 text-sm text-emerald-600">
                        <CheckCircle2 className="w-4 h-4" />
                        <span>Paid on {new Date(invoice.paid_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Floating Batch Pay Bar */}
      {selectedCount > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-slate-900 text-white rounded-2xl shadow-2xl px-6 py-4 flex items-center gap-4 min-w-[320px] max-w-lg">
          <div className="flex-1">
            <p className="font-semibold text-sm">{selectedCount} invoice{selectedCount > 1 ? 's' : ''} selected</p>
            <p className="text-slate-300 text-xs">Total: ${selectedTotal.toFixed(2)}</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-slate-400 hover:text-white"
            onClick={() => setSelectedIds(new Set())}
          >
            <XCircle className="w-4 h-4" />
          </Button>
          <Button
            className="bg-white text-slate-900 hover:bg-slate-100 rounded-xl font-semibold"
            onClick={openBatchPay}
          >
            <CreditCard className="w-4 h-4 mr-2" />
            Pay ${selectedTotal.toFixed(2)}
          </Button>
        </div>
      )}

      {/* Payment Dialog */}
      <Dialog open={paymentDialog} onOpenChange={(open) => {
        setPaymentDialog(open);
        if (!open) { setPayingInvoice(null); setPayingBatch(null); }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{payingBatch ? `Pay ${payingBatch.length} Invoices` : 'Pay Invoice'}</DialogTitle>
            <DialogDescription>
              {payingInvoice && !payingBatch && <>Invoice #{payingInvoice.invoice_number} - {payingInvoice.description}</>}
              {payingBatch && <>Combined payment for {payingBatch.length} selected invoices</>}
            </DialogDescription>
          </DialogHeader>
          
          {loadingPaymentMethods ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : (payingInvoice || payingBatch) ? (
            <Elements stripe={stripePromise}>
              <PaymentForm
                invoice={payingBatch ? payingBatch[0] : payingInvoice}
                invoices={payingBatch}
                paymentMethods={paymentMethods}
                token={token}
                onSuccess={() => {
                  setPaymentDialog(false);
                  setPayingInvoice(null);
                  setPayingBatch(null);
                  setSelectedIds(new Set());
                  fetchInvoices();
                }}
                onCancel={() => {
                  setPaymentDialog(false);
                  setPayingInvoice(null);
                  setPayingBatch(null);
                }}
              />
            </Elements>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoicesTab;
